/**
 * useRelatorioContagemData - Data fetching and state management for per-job specific reports
 */
import { useState, useCallback } from 'react';
import { msg } from '../utils/messages';
import {
  getResultadoContagem,
  getResumoComparativo,
  getColetaContagem,
  getUltimas3Detalhado,
  downloadRelatorioContagem,
} from '../services/relatorioContagemService';

export type ContagemReportId = 'resultado' | 'resumo' | 'coleta' | 'ultimas3';

export function useRelatorioContagemData(jobId: number) {
  const [selectedReport, setSelectedReport] = useState<ContagemReportId | null>(null);
  const [reportData, setReportData] = useState<any[]>([]);
  const [reportMeta, setReportMeta] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const generateReport = useCallback(async (clienteId?: number, dataFim?: string) => {
    if (!selectedReport) return;

    setLoading(true);
    setError(null);
    setReportData([]);
    setReportMeta(null);

    try {
      let result: any;
      switch (selectedReport) {
        case 'resultado':
          // Filtra por cliente selecionado no topo (contagens multi-cliente)
          result = await getResultadoContagem(jobId, clienteId);
          setReportData(result.data || []);
          setReportMeta({ hospital: result.hospital, titulo: result.titulo, data_contagem: result.data_contagem, total_items: result.total_items });
          break;
        case 'resumo':
          result = await getResumoComparativo(jobId, clienteId);
          setReportData(result.contagens || []);
          setReportMeta(result);
          break;
        case 'coleta':
          // Story 2.42: passa clienteId para filtrar por cliente especifico
          // (resolve bug Multi-cliente: logo/dados/items de cliente errado)
          result = await getColetaContagem(jobId, clienteId);
          setReportData(result.data || []);
          setReportMeta({
            hospital: result.hospital,
            data_contagem: result.data_contagem,
            empresa: result.empresa,
            codigo_cliente: result.codigo_cliente,
            lacres: result.lacres || [],
            total_items: result.total_items,
          });
          break;
        case 'ultimas3':
          result = await getUltimas3Detalhado(jobId, dataFim);
          setReportData(result.data || []);
          setReportMeta({ hospital: result.hospital, contagens: result.contagens, total_items: result.total_items });
          break;
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message || 'Erro ao gerar relatório';
      setError(detail);
      msg.error(detail);
    } finally {
      setLoading(false);
    }
  }, [selectedReport, jobId]);

  const downloadReport = useCallback(async (format: 'pdf' | 'excel', clienteId?: number, dataFim?: string) => {
    if (!selectedReport) return;

    setDownloading(format);
    try {
      let url = '';
      let filename = '';
      const clienteParam = clienteId ? `&cliente_id=${clienteId}` : '';

      switch (selectedReport) {
        case 'resultado':
          // Filtra o PDF/Excel pelo cliente selecionado (contagens multi-cliente)
          url = `/relatorios/contagem-completa/${jobId}?format=${format}${clienteParam}`;
          filename = `resultado_contagem_${jobId}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
          break;
        case 'resumo':
          url = `/relatorios/conferencia/${jobId}?format=${format}${clienteParam}`;
          filename = `resumo_comparativo_${jobId}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
          break;
        case 'coleta':
          // Story 2.42: passa cliente_id no URL para filtrar por cliente
          url = `/relatorios/solicitacao-coleta/${jobId}?format=${format}${clienteParam}`;
          filename = `solicitacao_coleta_${jobId}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
          break;
        case 'ultimas3':
          url = `/relatorios/ultimas-3-detalhado/${jobId}?format=${format}${dataFim ? `&data_fim=${dataFim}` : ''}`;
          filename = `ultimas_3_detalhado_${jobId}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
          break;
      }

      await downloadRelatorioContagem(url, filename);
    } catch (err: any) {
      msg.error('Erro ao baixar relatório. Tente novamente.');
    } finally {
      setDownloading(null);
    }
  }, [selectedReport, jobId]);

  const clearReport = useCallback(() => {
    setSelectedReport(null);
    setReportData([]);
    setReportMeta(null);
    setError(null);
  }, []);

  return {
    selectedReport,
    setSelectedReport,
    reportData,
    reportMeta,
    loading,
    error,
    downloading,
    generateReport,
    downloadReport,
    clearReport,
  };
}
